'use client';

import React from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Tooltip,
  Typography,
  CssBaseline,
  ThemeProvider,
  type SelectChangeEvent,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { FieldOption } from '../types/vtk';
import { FIELD_OPTIONS } from '../types/vtk';
import { createThemeFromTokens } from '@fast-computing/fast-graphics/mui-theme';
import FlowRatePlot from './flowRatePlot';

type ControlPanelProps = {
  timeROM: number;
  bcROM: number;
  field: FieldOption;
  loading: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onTimeChange: (_: Event, value: number | number[]) => void;
  onBcChange: (_: Event, value: number | number[]) => void;
  onFieldChange: (event: SelectChangeEvent<FieldOption>) => void;
  onFetchData: () => void;
  onOpenDescription: () => void;
  onUndo: () => void;
  onRedo: () => void;
};

const selectedTheme = createThemeFromTokens('atlas', {
  withComponentDefaults: true,
});

export default function ControlPanel({
  timeROM,
  bcROM,
  field,
  loading,
  canUndo,
  canRedo,
  onTimeChange,
  onBcChange,
  onFieldChange,
  onFetchData,
  onOpenDescription,
  onUndo,
  onRedo,
}: ControlPanelProps) {
  return (
    <ThemeProvider theme={selectedTheme}>
      <CssBaseline />
      <Box
        sx={{
          pt: 1,
          pl: 2,
          pr: 2,
          bgcolor: `${selectedTheme.palette.background.default}`,
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflow: 'hidden',
        }}
      >
        <Typography variant='h6' gutterBottom>
          Left Atrium Appendage Simulator
        </Typography>

        <Typography
          variant='body2'
          color={`${selectedTheme.palette.text.primary}`}
          fontFamily={`${selectedTheme.typography.fontFamily}`}
          sx={{ fontSize: '0.95rem' }}
        >
          Reduced-order simulations of left atrial appendage flow highlight
          blood stasis patterns in a patient with atrial fibrillation. Age M1,
          Age M2, and Washout identify regions where old blood persists and
          thrombus risk may increase.
        </Typography>

        <Box>
          <Button
            variant='text'
            size='small'
            onClick={onOpenDescription}
            sx={{ px: 0 }}
          >
            Read Full Description
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: '0 0 auto',
          }}
        >
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 1,
              }}
            >
              <Typography variant='body2' sx={{ mb: 0 }}>
                Cardiac Cycle [s]: {timeROM.toFixed(2)}
              </Typography>

              <Tooltip
                title='Duration of the heartbeats simulated in the model.'
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: '0.9rem',
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    cursor: 'help',
                  }}
                >
                  <InfoOutlinedIcon fontSize='small' />
                </Box>
              </Tooltip>
            </Box>
            <Slider
              value={timeROM}
              min={0} //{3.321}
              max={5.4 - 3.321}
              step={0.01}
              valueLabelDisplay='auto'
              onChange={onTimeChange}
              disabled={loading}
            />
          </Box>

          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 1,
              }}
            >
              <Typography variant='body2' sx={{ mb: 0 }}>
                Flux Intensity: {bcROM.toFixed(2)}
              </Typography>

              <Tooltip
                title='Magnitude of blood flow velocity.'
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      maxWidth: 300,
                      fontSize: '0.9rem',
                      lineHeight: 1.4,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    cursor: 'help',
                  }}
                >
                  <InfoOutlinedIcon fontSize='small' />
                </Box>
              </Tooltip>
            </Box>
            <Slider
              value={bcROM}
              min={0.5}
              max={1.5}
              step={0.01}
              valueLabelDisplay='auto'
              onChange={onBcChange}
              disabled={loading}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel id='field-select-label'>Loaded ROM</InputLabel>
            <Select
              labelId='field-select-label'
              value={field}
              label='Loaded ROM'
              onChange={onFieldChange}
              disabled={loading}
            >
              {FIELD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant='contained'
              size='large'
              onClick={onFetchData}
              disabled={loading}
              fullWidth
            >
              {loading ? 'Loading...' : 'Start Simulation'}
            </Button>

            <Button
              variant='outlined'
              size='medium'
              onClick={onUndo}
              disabled={loading || !canUndo}
              sx={{ minWidth: 72, px: 4 }}
            >
              Previous
            </Button>

            <Button
              variant='outlined'
              size='medium'
              onClick={onRedo}
              disabled={loading || !canRedo}
              sx={{ minWidth: 72, px: 4 }}
            >
              Next
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${selectedTheme.palette.background.paper}`,
            borderRadius: 2,
            p: 1,
          }}
        >
          <FlowRatePlot timeROM={timeROM} bcROM={bcROM} />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
